import { DetectorRequestBody } from 'components/model-runner-modal/detector-runner';
import { ObjectState, getCore } from 'cvat-core-wrapper';
import { ObjectType, ShapeType } from 'cvat-core/src/enums';
import { Label } from 'cvat-core/src/labels';
import MLModel from 'cvat-core/src/ml-model';
import { Job } from 'cvat-core/src/session';

const core = getCore();

function loadLabelAttributes(
    attributes: { name: string; value: string }[],
    label: Label,
): Record<number, string> {
    return attributes.reduce((acc, { name, value }) => {
        const attributeSpec = label.attributes.find((_attr) => _attr.name === name);

        if (!attributeSpec) {
            return acc;
        }

        switch (attributeSpec.inputType) {
            case 'number': {
                const [min, max, step] = attributeSpec.values;
                if (
                    Number.isFinite(+value) &&
                    +value >= +min &&
                    +value <= +max &&
                    !(+value % +step)
                ) {
                    return {
                        ...acc,
                        [attributeSpec.id as number]: `${value}`,
                    };
                }

                return acc;
            }
            case 'text': {
                return {
                    ...acc,
                    [attributeSpec.id as number]: `${value}`,
                };
            }
            case 'select':
            case 'radio': {
                if (attributeSpec.values.includes(value)) {
                    return {
                        ...acc,
                        [attributeSpec.id as number]: value,
                    };
                }
                return acc;
            }
            case 'checkbox':
                return {
                    ...acc,
                    [attributeSpec.id as number]: `${value}`.toLowerCase() === 'true' ? 'true' : 'false',
                };
            default:
                return acc;
        }
    }, {} as Record<number, string>);
}

export async function detectFrame(
    job: Job, frame: number, curZOrder: number,
    model: MLModel, body: DetectorRequestBody,
): Promise<Array<ObjectState>> {
    const result = await core.lambda.call(job.taskId, model, {
        ...body, frame, job: job.id,
    });

    type SerializedShape = {
        type: ShapeType;
        rotation?: number;
        attributes: { name: string; value: string }[];
        label: string;
        outside?: boolean;
        points?: number[];
        mask?: number[];
        elements: SerializedShape[];
    };

    const states = result.map(
        (data: SerializedShape): ObjectState | null => {
            const jobLabel = job.labels
                .find((jLabel: Label): boolean => jLabel.name === data.label);

            if (!jobLabel) return null;

            const objectData = {
                label: jobLabel,
                objectType: ObjectType.SHAPE,
                frame,
                occluded: false,
                rotation: [
                    ShapeType.RECTANGLE, ShapeType.ELLIPSE,
                ].includes(data.type) ? (data.rotation || 0) : 0,
                source: core.enums.Source.AUTO,
                attributes: loadLabelAttributes(data.attributes, jobLabel),
                zOrder: curZOrder,
            };

            if (data.type === ShapeType.SKELETON && jobLabel.type === ShapeType.SKELETON) {
                // find a center of the skeleton
                // to set this center as outside points position
                const center = data.elements.reduce<[number, number]>((acc, { points }) => {
                    if (points) {
                        return [acc[0] + points[0], acc[1] + points[1]];
                    }
                    return acc;
                }, [0, 0]).map((el) => el / (data.elements.length || 1));

                const elements = (jobLabel.structure?.sublabels || []).map((sublabel) => {
                    const element = data.elements.find((el) => el.label === sublabel.name);
                    return {
                        label: sublabel,
                        objectType: ObjectType.SHAPE,
                        shapeType: sublabel.type,
                        attributes: {},
                        frame,
                        source: core.enums.Source.AUTO,
                        points: [...center],
                        occluded: false,
                        outside: true,
                        ...(element ? {
                            attributes: loadLabelAttributes(element.attributes, sublabel),
                            points: element.points,
                            outside: !!element.outside || false,
                        } : {}),
                    };
                }).map((elementData) => new core.classes.ObjectState({ ...elementData }));

                if (elements.every((element) => element.outside)) {
                    return null;
                }

                return new core.classes.ObjectState({
                    ...objectData,
                    shapeType: ShapeType.SKELETON,
                    points: [],
                    elements,
                });
            }

            if (data.type === 'mask' && data.points && body.convMaskToPoly) {
                return new core.classes.ObjectState({
                    ...objectData,
                    shapeType: 'polygon',
                    points: data.points,
                });
            }

            if (data.type === 'mask') {
                const [left, top, right, bottom] = (data.mask as number[]).splice(-4);
                const rle = core.utils.mask2Rle(data.mask);
                rle.push(left, top, right, bottom);
                return new core.classes.ObjectState({
                    ...objectData,
                    shapeType: data.type,
                    points: rle,
                });
            }

            return new core.classes.ObjectState({
                ...objectData,
                shapeType: data.type,
                points: data.points,
            });
        },
    ).filter((state: any) => state);

    return states.filter((state: ObjectState | null) => !!state);
}
